import { getAuthHeaders } from '@/identity.js';

/** The caller's web push subscriptions in cti-conversation-service (MEDA-1208). */
const ENDPOINT = '/v1/me/push-subscriptions';

/** Whether web push is on and the VAPID public key to subscribe with. */
export interface PushKey {
  enabled: boolean;
  publicKey: string | null;
}

/** The parts of `PushSubscription.toJSON()` the server stores. */
export interface BrowserPushSubscription {
  endpoint?: string;
  keys?: Record<string, string>;
}

/** Platform-JWT bearer plus a JSON content type. */
async function jsonHeaders(): Promise<Record<string, string>> {
  return { 'Content-Type': 'application/json', ...(await getAuthHeaders()) };
}

/** Reads whether web push is on; a failed read counts as off, so the bell stays hidden. */
export async function fetchPushKey(): Promise<PushKey> {
  try {
    const res = await fetch(`${ENDPOINT}/key`, {
      headers: await jsonHeaders(),
    });
    if (!res.ok) return { enabled: false, publicKey: null };
    const body = (await res.json()) as {
      enabled?: boolean;
      public_key?: string | null;
    };
    return {
      enabled: body.enabled === true && Boolean(body.public_key),
      publicKey: body.public_key ?? null,
    };
  } catch {
    return { enabled: false, publicKey: null };
  }
}

/** Stores this browser's subscription for the caller; the server keeps the endpoint and keys only. */
export async function savePushSubscription(
  subscription: BrowserPushSubscription,
): Promise<void> {
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: await jsonHeaders(),
    body: JSON.stringify({
      endpoint: subscription.endpoint,
      keys: {
        p256dh: subscription.keys?.p256dh,
        auth: subscription.keys?.auth,
      },
    }),
  });
  if (!res.ok)
    throw new Error(`Could not save the push subscription (${res.status})`);
}

/** Removes this browser's subscription for the caller. */
export async function deletePushSubscription(endpoint: string): Promise<void> {
  const res = await fetch(ENDPOINT, {
    method: 'DELETE',
    headers: await jsonHeaders(),
    body: JSON.stringify({ endpoint }),
  });
  if (!res.ok)
    throw new Error(`Could not remove the push subscription (${res.status})`);
}
